import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import ConfirmationModalNew from "../../../../common/ConfirmationModalNew";
import CustomInput from "../../../../common/CustomInput";
import { formatDateToYYYYMMDD, getTimeDifference } from "../../../../../helpers/functions";

const BILLING_OPTIONS = [
  { value: "", label: "Select Billing Information" },
  { value: "pending", label: "Pending State : 93899991" },
  { value: "completed", label: "Completed" },
  { value: "cancelled", label: "Cancelled" }
];

const InterventionCompletion = ({
  open = false,
  onClose = () => {},
  onConfirm = () => {},
  title = "Confirm Intervention Complete",
  confirmText = "Accept Pending State",
  cancelText = "Cancel",
  intervention,
  // Default the billing dropdown to "pending" so the modal opens pre-selected
  billingInformation = "pending",
  onBillingInformationChange = () => {},
  className = "!w-[598px] !max-w-[598px] !h-auto max-h-[640px]"
}) => {
  const [acceptPending, setAcceptPending] = useState(false);

  const usersState = useSelector((state) => state.users ?? {});
  const currentLoggedInUserDetail = usersState.currentLoggedInUserDetail;
  const pharmacistName = [currentLoggedInUserDetail?.firstName, currentLoggedInUserDetail?.lastName]
    .filter(Boolean)
    .join(" ") || "";

  const createdDate = intervention?.createdDate || intervention?.createddate || new Date().toISOString();
  const startDateTime = intervention?.interventionAssessment?.interventionAssessment?.startDateTime
    || intervention?.interventionAssessment?.interventionAssessmentForm?.startDateTime
    || createdDate;
  // Use current time for live time spent until finalized
  const endDateTime = new Date().toISOString();
  const existingBillingRaw =
    intervention?.interventionAssessment?.interventionAssessment?.notification
      ?.billingInformation ??
    intervention?.interventionAssessment?.interventionAssessmentForm
      ?.notification?.billingInformation ??
    billingInformation;

  // `CustomInput` expects the dropdown `value` to exactly match one of BILLING_OPTIONS[].value.
  // Treat empty string/whitespace as "pending" so the label shows correctly.
  const existingBilling =
    typeof existingBillingRaw === "string" && existingBillingRaw.trim() !== ""
      ? existingBillingRaw
      : "pending";

  const disabledConfirm = !acceptPending || !existingBilling;

  return (
    <ConfirmationModalNew
      open={open}
      title={title}
      className={className}
      confirmText={confirmText}
      cancelText={cancelText}
      disableConfirm={disabledConfirm}
      contentClassName="pb-8"
      actionsClassName="!pb-3"
      belowActionsContentClassName="px-8 pb-3"
      content={() => (
        <div>
          <div className="w-[534px] h-[48px] rounded-[4px] grid grid-cols-2 items-center">
            <div className="text-gray-600">
              <span className="font-semibold">Date:</span>{" "}
              <span className="font-semibold">{formatDateToYYYYMMDD(new Date(createdDate))}</span>
            </div>
            <div className="text-gray-600 text-right">
              <span className="font-semibold">Time Spent:</span>{" "}
              <span className="font-semibold">{getTimeDifference(startDateTime, endDateTime)}</span>
            </div>
          </div>
          <div className="flex justify-start">
            <div className="w-[534px] h-[48px] rounded-[4px] gap-8 flex items-center">
              <span className="text-gray-600">
                <span className="font-semibold">Pharmacist Name:</span>{' '}
                <span className="font-semibold">{pharmacistName || ''}</span>
              </span>
            </div>
          </div>

          {/* Billing Select + description + reference */}
          <CustomInput
            options={BILLING_OPTIONS}
            name="billingInformation"
            className="col-span-12 w-[534px] h-[70px] p-3 border border-yellow-300 bg-yellow-50 rounded-[4px] text-gray-800"
            label="Billing Information"
            type="dropdown"
            value={existingBilling}
            required
            onChange={(e) => onBillingInformationChange(e.value)}
            shortMenuHeight
          />
          <div className="mt-0" role="group" aria-label="Billing information selection">
            <div className="w-[534px] h-[52px] py-4 mb-4 text-gray-800">
              <p className="text-sm font-normal leading-5 font-inter">
                Prescription not filled as prescribed due to a clinical issue or confirmed as a falsified prescription.
              </p>
            </div>
            <div className="border-t border-gray-200 my-4" />
            <label className="flex items-start gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                className="mt-1 h-4 w-4 text-teal-600 focus:ring-teal-600 border-gray-300 rounded"
                checked={acceptPending}
                onChange={(e) => setAcceptPending(e.target.checked)}
              />
              <span className="text-gray-800 text-sm">
                By selecting <span className="font-semibold">"Accept Pending State"</span>, you are confirming intervention as final — cannot be edited later, excluding the "Medication Record".
              </span>
            </label>
          </div>
        </div>
      )}
      onCross={onClose}
      handleClose={onClose}
      confirmAction={onConfirm}
      belowActionsContent={() => (
        <div className="flex items-center justify-between gap-2 leading-tight my-4 mt-0">
          <span className="text-gray-800 text-sm">
            It is the pharmacist's responsibility to ensure PIN is accurate.
          </span>
          <a className="underline text-teal-300 font-semibold text-sm shrink-0" href="#" target="_blank" rel="noopener noreferrer">
            Reference
          </a>
        </div>
      )}
    />
  );
};

export default InterventionCompletion;
